import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import useFocusTrap from '../hooks/useFocusTrap';
import { getFeaturedAnnouncements } from '../services/api';
import NavDropdown from './NavDropdown';
import SocialLinks from './SocialLinks';
import './Navbar.css';
import { Button } from './Button';
import defaultLogo from '../new_logo-removebg-preview.png';

// P11: replaces the old "Other ▾" dropdown (a label that communicated
// nothing) with three named, described groupings. Destinations moved here
// from the old dynamic per-country flyout — it now points at a real
// /destinations listing page instead of duplicating a live country list
// in the nav itself.
const STUDY_ABROAD_ITEMS = [
  { label: 'Universities', to: '/universities', description: 'Browse our partner universities' },
  { label: 'Programs', to: '/programs', description: 'Search degree programs by subject' },
  { label: 'Scholarships', to: '/scholarships', description: 'Tuition discounts and scholarship offers' },
  { label: 'Destinations', to: '/destinations', description: 'Countries we place students in' },
];

const SERVICES_ITEMS = [
  { label: 'Our Services', to: '/services', description: 'Applications, visas, and post-arrival support' },
  { label: 'Medical Tourism', to: '/medical-tourism', description: 'Treatment abroad, with trusted hospitals and full support' },
  { label: 'Pricing', to: '/pricing', description: 'Our service packages and fees' },
  { label: 'Partner With Us', to: '/partner-with-us', description: 'Agencies and schools we work with' },
];

const RESOURCES_ITEMS = [
  { label: 'Blog', to: '/blog', description: 'Guides on applications, visas, and life abroad' },
  { label: 'Announcements', to: '/announcements', description: 'Open intakes, deadlines, and scholarships' },
  { label: 'Success Stories', to: '/stories', description: 'Real students, real outcomes' },
];

function Navbar() {
  const { globalContent } = useSiteContent();
  const navigate = useNavigate();
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  // Single source of truth for which of the three nav dropdowns is open —
  // guarantees at most one is ever open, so moving from one trigger straight
  // onto the next can't render both at once during the hover-out grace delay.
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const menuButtonRef = useRef(null);
  const navMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  // Same featured/list call HomeAnnouncements already makes — the shared
  // request cache (services/cache.js) dedupes/serves this from its own
  // fetch within the TTL window, so the badge never costs a second request.
  useEffect(() => {
    let cancelled = false;
    getFeaturedAnnouncements(3)
      .then((data) => { if (!cancelled) setAnnouncementCount(Array.isArray(data) ? data.length : 0); })
      .catch(() => { if (!cancelled) setAnnouncementCount(0); });
    return () => { cancelled = true; };
  }, []);

  const logo = globalContent.header_logo_url || defaultLogo;
  const brandName = globalContent.company_name || 'VisionExploit';
  const [click, setClick] = useState(false);
  const [button, setButton] = useState(true);
  const clickHandlerRef = useRef(null);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setClick((prevClick) => !prevClick);
  };

  const closeMobileMenu = () => {
    setClick(false);
    setSearchOpen(false);
    setOpenDropdown(null);
  };

  useFocusTrap(navMenuRef, click, closeMobileMenu);

  const showButton = () => {
    if (window.innerWidth <= 960) {
      setButton(false);
    } else {
      setButton(true);
      setClick(false);
    }
  };

  useEffect(() => {
    showButton();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', showButton);
    return () => window.removeEventListener('resize', showButton);
  }, []);

  useEffect(() => {
    if (!click) {
      if (clickHandlerRef.current) {
        document.removeEventListener('click', clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      const handleDocumentClick = (e) => {
        const navbar = document.querySelector('.navbar');
        const menuIcon = document.querySelector('.menu-icon');
        const navMenu = document.querySelector('.nav-menu');

        if (menuIcon && menuIcon.contains(e.target)) {
          return;
        }

        if (navMenu && navMenu.contains(e.target)) {
          return;
        }

        if (navbar && !navbar.contains(e.target)) {
          setClick(false);
        }
      };

      document.addEventListener('click', handleDocumentClick);
      clickHandlerRef.current = handleDocumentClick;
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (clickHandlerRef.current) {
        document.removeEventListener('click', clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    };
  }, [click]);

  useEffect(() => {
    if (click) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [click]);

  const toggleSearch = () => {
    setSearchOpen((prev) => {
      const next = !prev;
      if (next) requestAnimationFrame(() => searchInputRef.current?.focus());
      return next;
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/programs?search=${encodeURIComponent(q)}` : '/programs');
    setSearchQuery('');
    setSearchOpen(false);
    closeMobileMenu();
  };

  return (
    <>
      <header>
      <nav className="navbar" aria-label="Main">
        <div className="navbar-container">
          <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
            <img
              src={logo}
              alt={`${brandName} logo`}
              className="navbar-logo-img"
              width={34}
              height={34}
              decoding="async"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultLogo;
              }}
            />
            {brandName}
          </Link>

          <div className="navbar-icon-actions">
            <button
              type="button"
              className="search-icon-btn"
              onClick={toggleSearch}
              aria-expanded={searchOpen}
              aria-controls="navbar-search-form"
              aria-label={searchOpen ? 'Close search' : 'Search programs'}
            >
              <Search size={20} aria-hidden="true" />
            </button>
            {/* Wide screens only (CSS): the desktop bar has no room below 1440px. */}
            <SocialLinks listClassName="navbar-social" linkClassName="navbar-social-link" size={15} />
          </div>

          {/* Sibling of .navbar-icon-actions, not a child — .menu-icon is
              position:absolute and needs .navbar-container (the nearest
              positioned ancestor here) as its containing block. Nesting it
              inside .navbar-icon-actions (also position:relative, for the
              search dropdown's own anchor) made it position against that
              narrow search-button-only row instead, landing it almost on
              top of the search icon on mobile. */}
          <button
            type="button"
            className="menu-icon"
            onClick={handleClick}
            aria-expanded={click}
            aria-controls="primary-navigation"
            aria-label={click ? 'Close menu' : 'Open menu'}
            ref={menuButtonRef}
          >
            {click ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
          </button>

          {searchOpen && (
            <form id="navbar-search-form" className="navbar-search-form" onSubmit={handleSearchSubmit} role="search">
              <label htmlFor="navbar-search-input" className="sr-only">Search programs</label>
              <input
                id="navbar-search-input"
                ref={searchInputRef}
                type="search"
                className="navbar-search-input"
                placeholder="Search programs, e.g. Engineering"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="navbar-search-submit">
                <Search size={16} aria-hidden="true" />
                <span>Search</span>
              </button>
            </form>
          )}

          <ul
            id="primary-navigation"
            className={click ? 'nav-menu active' : 'nav-menu'}
            aria-hidden={!button && !click}
            ref={navMenuRef}
          >
            <li className="nav-item">
              <Link to="/" className="nav-links" onClick={closeMobileMenu}>
                Home
              </Link>
            </li>

            <NavDropdown
              label="Study Abroad"
              items={STUDY_ABROAD_ITEMS}
              onNavigate={closeMobileMenu}
              isOpen={openDropdown === 'Study Abroad'}
              onOpen={() => setOpenDropdown('Study Abroad')}
              onClose={() => setOpenDropdown((prev) => (prev === 'Study Abroad' ? null : prev))}
            />
            <NavDropdown
              label="Services"
              items={SERVICES_ITEMS}
              onNavigate={closeMobileMenu}
              isOpen={openDropdown === 'Services'}
              onOpen={() => setOpenDropdown('Services')}
              onClose={() => setOpenDropdown((prev) => (prev === 'Services' ? null : prev))}
            />
            <NavDropdown
              label="Resources"
              items={RESOURCES_ITEMS}
              onNavigate={closeMobileMenu}
              badgeCount={announcementCount}
              isOpen={openDropdown === 'Resources'}
              onOpen={() => setOpenDropdown('Resources')}
              onClose={() => setOpenDropdown((prev) => (prev === 'Resources' ? null : prev))}
            />

            <li className="nav-item">
              <Link to="/about" className="nav-links" onClick={closeMobileMenu}>
                About
              </Link>
            </li>

            <li className="nav-item nav-item-cta-group">
              <Link to="/sign-up" className="nav-links-mobile nav-links-mobile--secondary" onClick={closeMobileMenu}>
                Register
              </Link>
              <Link to="/consultation" className="nav-links-mobile nav-links-mobile--primary" onClick={closeMobileMenu}>
                Book free consultation
              </Link>
            </li>

            <li className="nav-item nav-item-social">
              <SocialLinks listClassName="nav-social" linkClassName="nav-social-link" size={18} />
            </li>
          </ul>

          {button && (
            <div className="navbar-cta-group">
              <Button buttonStyle="btn--outline" to="/sign-up">
                Register
              </Button>
              <Button buttonStyle="btn--primary" to="/consultation">
                Book free consultation
              </Button>
            </div>
          )}
        </div>
      </nav>
      </header>
    </>
  );
}

export default Navbar;
